import {
  MicroFrontendWrapper,
  useApplicationStateService,
  useAppStateProperty,
  useDependencies,
} from '@inform-appshell/fusion-kit-react';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router';
import './assets/css/themes/globals.css';
import {
  BusyOverlay,
  FeedbackHost,
  LoadingScreen,
  ShellChrome,
  railBtnSx,
  type RailModule,
} from '@inform-appshell/mui-appshell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, List, ListItemButton, ListItemText, Stack, Tooltip, Typography, type Theme } from '@mui/material';
import { LanguageSwitch } from './components/LanguageSwitch/LanguageSwitch';
import { ThemeToggle } from './components/ThemeToggle/ThemeToggle';
import { UserMenu } from './components/UserMenu';
import { useTheme } from './hooks/useTheme';
import { icons } from './theme/icons';
import { ShellFrameAdapter } from './utils/ShellFrameAdapter';
import HomePage from './views/HomePage';
import NotFoundPage from './views/NotFoundPage';

import { getRemoteModulePath, RemoteModuleHealthService } from '@inform-appshell/fusion-kit-module-federation';
import { ActiveModuleEventsArgs } from './interfaces/ActiveModuleEventsArgs';
import { RouteConfig } from './interfaces/RouteConfig';
import { createRouterExtension } from './utils/RouterExtension';
import { resolveShellEmbed } from './utils/resolveEmbedParams';
import { subscribe as subscribeHistory } from './utils/historyPatch';
import { getAllBootstrapableModules, getRemotesConfigFromConfigManager } from './utils/utils';

import type { ModuleMenuItem } from '@inform-appshell/fusion-kit-contracts';
import type { ShellModule } from './interfaces/ShellModule';

import { version as appVersion } from '../package.json';

// GB30 ships no MuiListItemButton override, so a selected nav item falls back to MUI's
// `action.selected` tint — too faint against the panel to read as the active page. States it
// explicitly instead: a leading accent bar plus a weighted, primary-coloured label.
const NAV_ITEM_SX = {
  '&.Mui-selected': {
    'bgcolor': 'action.selected',
    'borderInlineStart': '3px solid',
    'borderColor': 'primary.main',
    '& .MuiListItemText-primary': { fontWeight: 600, color: 'primary.main' },
    '&:hover': { bgcolor: 'action.selected' },
  },
  // Keeps the label aligned with the selected item, which the accent bar would otherwise indent.
  '&:not(.Mui-selected)': { borderInlineStart: '3px solid transparent' },
} as const;

// Page background: white in light; in dark the near-black page ground, so paper surfaces (tables,
// cards) stand out on it.
const PAGE_SURFACE_SX = (theme: Theme) => ({
  bgcolor: 'background.paper',
  ...theme.applyStyles('dark', { bgcolor: 'var(--surface-ground)' }),
});

// The rail's bottom cluster. `flexShrink: 0` keeps it pinned while the module list above scrolls;
// each slot matches `railBtnSx`'s 40px box so custom content lines up with the module buttons.
const RAIL_END_SX = { display: 'flex', flexDirection: 'column', flexShrink: 0 } as const;
const RAIL_SLOT_SX = {
  height: railBtnSx.height,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
} as const;

interface AppContentProps {
  modules: ShellModule[];
  selectedModule: ShellModule | undefined;
  onSelectedModuleChange: (module: ShellModule | undefined) => void;
  isLoading: boolean;
  dynamicRoutes: RouteConfig[];
  activeMenuItems: ModuleMenuItem[];
  navigateRef: React.MutableRefObject<ReturnType<typeof useNavigate> | null>;
  onSelectModule: (name: string) => void;
}

function AppContent({
  modules,
  selectedModule,
  onSelectedModuleChange,
  isLoading,
  dynamicRoutes,
  activeMenuItems,
  navigateRef,
  onSelectModule,
}: AppContentProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const appDependencies = useDependencies();
  const { t } = useTranslation();
  const [modulesReady] = useAppStateProperty<boolean>('modulesReady', false);
  const [currentUser, setCurrentUser] = useState('');
  const [navOpen, setNavOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isDark, toggleTheme } = useTheme();
  const appName = t('navigation.appName');

  // The rail renders name/label/icon only; the routing fields stay on ShellModule.
  const railModules: RailModule[] = useMemo(
    () =>
      modules.map((mod) => ({ name: mod.name, label: mod.label, icon: <FontAwesomeIcon icon={icons.usersGear} /> })),
    [modules]
  );

  // iframe embedding (issue #396): hide the shell chrome when embedded=true.
  const embedded = useRef(resolveShellEmbed().embedded === true).current;

  useEffect(() => {
    let isActive = true;

    (async () => {
      const username = (await appDependencies?.fusionApp?.auth?.getUserInfo())?.username ?? '';
      if (isActive) setCurrentUser(username);
    })();

    return () => {
      isActive = false;
    };
  }, [appDependencies?.fusionApp?.auth]);

  // Store navigate function in ref so it can be used outside of this component
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate, navigateRef]);

  // Sync selected module based on current route
  useEffect(() => {
    const currentPath = location.pathname;

    // Check if we're on a module route
    const matchedModule = modules.find((mod) => currentPath.startsWith(`/${mod.name}`));
    if (matchedModule && matchedModule !== selectedModule) {
      onSelectedModuleChange(matchedModule);
    } else if (!matchedModule && currentPath === '/' && selectedModule !== undefined) {
      // Only clear if not already cleared (prevents infinite loop)
      onSelectedModuleChange(undefined);
    }
  }, [location.pathname, modules, selectedModule, onSelectedModuleChange]);

  const mainContent = (
    // Not bordered: it is the page itself, not a card on it. Also painted on its own so the
    // chromeless (MFE / embedded) render gets the same background.
    <Box className="shell-content-card" sx={PAGE_SURFACE_SX}>
      <div className="shell-content-main" ref={contentRef}>
        {/* Confined to the content region, so the chrome stays usable while a module is busy. */}
        <BusyOverlay open={isLoading} container={() => contentRef.current} />

        {/* Router view equivalent */}
        <Routes>
          <Route path="/" element={<HomePage modules={modules} onModuleClick={(mod) => navigate(`/${mod.name}`)} />} />

          {/* Dynamic MFE routes */}
          {dynamicRoutes.map((route) => (
            <Route key={route.routeName} path={route.path} element={route.element} />
          ))}

          {/* MFE routes register as their modules load; until then an unmatched path is still loading, not 404. */}
          <Route path="*" element={modulesReady ? <NotFoundPage /> : <LoadingScreen />} />
        </Routes>
      </div>
    </Box>
  );

  // No chrome when running as an MFE, or in iframe-embedding mode (issue #396):
  // `?embedded=true` hides the chrome so only the module content shows.
  if (!appDependencies?.isStandAlone || embedded) {
    return mainContent;
  }

  // Shell navigation items
  const shellNavItems = [{ path: '/', label: t('common.home') }];

  // Longest-prefix-wins match of menu item ids against the current path
  // (id '' is a module-root sentinel that matches any route inside the module).
  const activeNavId = selectedModule
    ? activeMenuItems
        .filter((mi) => (location.pathname + '/').startsWith(`/${selectedModule.name}/${mi.id}${mi.id ? '/' : ''}`))
        .sort((a, b) => b.id.length - a.id.length)[0]?.id
    : undefined;

  return (
    <ShellChrome
      modules={railModules}
      selectedModuleName={selectedModule?.name}
      onSelectModule={onSelectModule}
      navOpen={navOpen}
      onToggleNav={() => setNavOpen((open) => !open)}
      // `brandMark` omitted on purpose: mui-appshell 0.2.3 defaults it to its own `BrandMark`, the
      // INFORM mark. Pass `<BrandMark onClick={…} />` to make it interactive.
      brand={
        <Tooltip title={`${appName} v${appVersion} | ${new Date().getFullYear()}`}>
          <Typography noWrap>{appName}</Typography>
        </Tooltip>
      }
      headerEnd={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {modulesReady && (
            <MicroFrontendWrapper moduleName="cuam/organization-picker" fusionApp={appDependencies.fusionApp} />
          )}
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
          <LanguageSwitch />
        </Stack>
      }
      navItems={
        <List component="nav" disablePadding>
          {activeMenuItems.length > 0
            ? activeMenuItems.map((menuItem) => (
                <ListItemButton
                  key={menuItem.id}
                  selected={menuItem.id === activeNavId}
                  disabled={!menuItem.canExecute}
                  onClick={() => menuItem.canExecute && menuItem.execute?.()}
                  sx={NAV_ITEM_SX}
                >
                  <ListItemText primary={menuItem.title} />
                </ListItemButton>
              ))
            : shellNavItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={NAV_ITEM_SX}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
        </List>
      }
      // Both go in the rail's bottom slot, admin above the user icon. Not `navEnd`: that renders in
      // the nav panel, so it sits beside the rail and disappears when the panel is collapsed.
      // mui-appshell has no dedicated rail-end slot — see §7 of the migration doc.
      userMenu={
        <Box sx={RAIL_END_SX}>
          {modulesReady && (
            // Clamped to the module buttons' geometry. The admin menu is an MFE and brings its own
            // `.cuam-root { height: 100% }`, which in an auto-height rail slot grows the whole
            // column until it scrolls and pushes the user icon out of view.
            <Box sx={RAIL_SLOT_SX}>
              <MicroFrontendWrapper moduleName="cuam/admin-menu" fusionApp={appDependencies.fusionApp} />
            </Box>
          )}
          <UserMenu currentUser={currentUser} onLogout={() => void appDependencies?.fusionApp?.auth.logout()} />
        </Box>
      }
      contentSx={[{ display: 'flex', flexDirection: 'column' }, PAGE_SURFACE_SX]}
    >
      {mainContent}
    </ShellChrome>
  );
}

function usePreserveRealmParam() {
  const realmRef = useRef(new URLSearchParams(window.location.search).get('realm'));

  useEffect(() => {
    if (!realmRef.current) return;
    const realm = realmRef.current;

    return subscribeHistory({
      transformUrl: (url) => {
        if (!url) return url;
        try {
          const parsed = new URL(typeof url === 'string' ? url : url.toString(), window.location.origin);
          if (!parsed.searchParams.has('realm')) {
            parsed.searchParams.set('realm', realm);
          }
          return parsed.pathname + parsed.search + parsed.hash;
        } catch {
          return url;
        }
      },
    });
  }, []);
}

export default function App() {
  usePreserveRealmParam();

  const [isLoading, setIsLoading] = useState(false);
  const [dynamicRoutes, setDynamicRoutes] = useState<RouteConfig[]>([]);
  const [modules, setModules] = useState<ShellModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<ShellModule | undefined>(undefined);
  const [activeMenuItems, setActiveMenuItems] = useState<ModuleMenuItem[]>([]);

  const appDependencies = useDependencies();
  const appStateService = useApplicationStateService();

  // Create RouterExtension instance
  const routerExtension = useMemo(() => {
    if (!appDependencies?.fusionApp) {
      throw new Error('FusionApp is required to create RouterExtension');
    }
    return createRouterExtension(appDependencies.fusionApp);
  }, [appDependencies?.fusionApp]);

  // Create health service once (not on every render)
  const remoteModuleHealthService = useRef(new RemoteModuleHealthService()).current;

  // Stable FrameAdapter instance — registered unconditionally so MFEs always get feedback
  const shellAdapter = useRef(new ShellFrameAdapter()).current;

  // Ref to ensure initialization runs only once
  const hasInitializedRef = useRef(false);

  // Ref to store navigate function from React Router
  const navigateRef = useRef<ReturnType<typeof useNavigate> | null>(null);

  // Unified callback for module selection changes (used by both AppContent route sync and handleActivateModule)
  const handleSelectedModuleChange = useCallback((module: ShellModule | undefined) => {
    setSelectedModule(module);
    setActiveMenuItems(module?.menuItems || []);
  }, []);

  //this is where the modules are activated and the route is pushed
  const handleActivateModule = useCallback(
    (activeModuleEventsArgs: ActiveModuleEventsArgs) => {
      if (!activeModuleEventsArgs.remoteModulePath) return;

      const module = appDependencies?.fusionApp?.remoteManager?.getLoadedModule(
        activeModuleEventsArgs.remoteModulePath
      );
      if (!module) {
        console.error(`Module with ID ${activeModuleEventsArgs.remoteModulePath} not found`);
        return;
      }

      // Navigate to the module using React Router (equivalent to router.push in Vue)
      if (navigateRef.current) {
        navigateRef.current(`/${module.name}`);
      }

      // Store active module in encrypted storage
      appDependencies?.fusionApp?.encryptedStorage?.setItem('active-module', activeModuleEventsArgs.remoteModulePath);

      // Find and set the selected module
      const shellModule = modules.find((m) => m.moduleFullPath === activeModuleEventsArgs.remoteModulePath);
      if (shellModule) {
        handleSelectedModuleChange(shellModule);
      }
    },
    [appDependencies?.fusionApp, modules, handleSelectedModuleChange]
  );

  const currentBasename = useBasename(appDependencies?.isStandAlone);

  // Register adapter and busy callback
  useEffect(() => {
    if (!appDependencies?.fusionApp) return;
    appDependencies.fusionApp.registerFrameAdapter(shellAdapter);

    appDependencies.fusionApp.registerIsBusyCallback((isBusy: boolean) => {
      setIsLoading(isBusy);
    });
  }, [appDependencies, shellAdapter]);

  // Redirect /cuam to /cuam/ to avoid routing conflicts
  useEffect(() => {
    if (!appDependencies?.isStandAlone && currentBasename === '/cuam' && window.location.pathname === '/cuam') {
      window.history.replaceState(null, '', '/cuam/');
    }
  }, [appDependencies?.isStandAlone, currentBasename]);

  //on mount [//TODO check is we can use an existing effect above]
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Register callback to update routes state
    routerExtension.registerRoutesChangeCallback((routes) => {
      setDynamicRoutes([...routes]);
    });

    // Event handler defined at effect scope level
    const handleModuleActivation = (event: Event) => {
      const customEvent = event as CustomEvent;
      handleActivateModule(customEvent.detail);
    };

    const initializeModules = async () => {
      if (!appDependencies?.fusionApp) return;

      const configManager = appDependencies?.fusionApp?.configurationManager;
      if (!configManager) {
        console.error('Configuration Manager is not available');
        setIsLoading(false);
        return;
      }

      const remotesConfiguration = getRemotesConfigFromConfigManager(configManager);

      if (!remotesConfiguration) {
        console.error('Remotes Configuration is not initialized');
        setIsLoading(false);
        return;
      }

      // Register the remotes with the RemoteModuleHealthService
      remoteModuleHealthService.registerRemotes(remotesConfiguration.remotes);

      appDependencies?.fusionApp?.encryptedStorage?.setItem('modules-loaded', 'false'); // Set the flag to true

      //setup remotemodule manager
      appDependencies?.fusionApp?.remoteManager?.configure(remotesConfiguration);
      //load all modules from all remotes
      const bootstrapableModules = getAllBootstrapableModules(remotesConfiguration);

      const loadedShellModules: ShellModule[] = [];

      // Create array of promises
      const loadPromises = bootstrapableModules.map(async ({ remote, module }) => {
        try {
          const loadedModule = await appDependencies.fusionApp.remoteManager?.loadModule(remote, module);
          if (loadedModule) {
            const modulePath = getRemoteModulePath(remote, module);
            routerExtension.addMfeRoute(modulePath, loadedModule.name);

            // The rail icon is supplied when the RailModule list is derived, not stored here.
            loadedShellModules.push({
              id: modulePath,
              name: loadedModule.name,
              label: loadedModule.title || loadedModule.name,
              moduleFullPath: modulePath,
              menuItems: loadedModule.menuItems || [],
              description: loadedModule.description,
            });
          }
        } catch (error) {
          console.error(`Failed to load bootstrap module ${remote}/${module}:`, error);
        }
      });

      // Wait for all modules to load
      await Promise.all(loadPromises);

      // Load utility modules (non-bootstrap modules needed for shell UI)
      // These are not added to the module list but are available for MicroFrontendWrapper
      try {
        await appDependencies.fusionApp.remoteManager?.loadModule('cuam', 'organization-picker');
      } catch (error) {
        console.error('Failed to load organization-picker module:', error);
      }

      // Update modules state
      setModules(loadedShellModules);

      remoteModuleHealthService.startHealthMonitoring();

      //handling the the switch between modules
      window.addEventListener('activate-module', handleModuleActivation);

      appStateService?.set('modulesReady', true);
      appDependencies.fusionApp.encryptedStorage?.setItem('modules-loaded', true);
      window.dispatchEvent(new Event('modules-loaded')); //activate the first module
    };

    initializeModules();

    return () => {
      window.removeEventListener('activate-module', handleModuleActivation);
    };
  }, []); // Empty array = runs only once on mount (like Vue's onMounted)

  // ShellChrome's rail reports the module by name. Deselection has no rail equivalent — the Home
  // nav item routes to `/`, and the route-sync effect in AppContent clears the selection.
  const handleSelectModule = useCallback(
    (name: string) => {
      const target = modules.find((mod) => mod.name === name);
      if (!target?.moduleFullPath) {
        console.warn('Module has no moduleFullPath:', name);
        return;
      }

      // Re-clicking the active module returns to its default entry — the first menu item it
      // registers, which for CUAM is Users. Activating it again would otherwise be a no-op, leaving
      // the rail looking unresponsive from a nested route.
      if (selectedModule?.name === name) {
        const [defaultItem] = target.menuItems ?? [];
        if (defaultItem?.canExecute) defaultItem.execute?.();
        else navigateRef.current?.(`/${name}`);
        return;
      }

      handleActivateModule({ remoteModulePath: target.moduleFullPath });
    },
    [handleActivateModule, modules, selectedModule]
  );

  // Always use BrowserRouter for proper history integration
  const RouterComponent = BrowserRouter;
  const routerProps = { basename: currentBasename };

  return (
    <RouterComponent {...routerProps}>
      <FeedbackHost source={shellAdapter.source} />
      <AppContent
        modules={modules}
        selectedModule={selectedModule}
        onSelectedModuleChange={handleSelectedModuleChange}
        isLoading={isLoading}
        dynamicRoutes={dynamicRoutes}
        activeMenuItems={activeMenuItems}
        navigateRef={navigateRef}
        onSelectModule={handleSelectModule}
      />
    </RouterComponent>
  );
}

function useBasename(isStandAlone?: boolean) {
  const subscribe = useCallback((callback: () => void) => {
    window.addEventListener('popstate', callback);

    const nativePushState = window.history.pushState;
    const nativeReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      nativePushState.call(window.history, ...args);
      callback();
    };
    window.history.replaceState = function (...args) {
      nativeReplaceState.call(window.history, ...args);
      callback();
    };

    return () => {
      window.removeEventListener('popstate', callback);
      window.history.pushState = nativePushState;
      window.history.replaceState = nativeReplaceState;
    };
  }, []);

  const getSnapshot = useCallback(() => {
    if (isStandAlone) return import.meta.env.VITE_BASE_URL || '';
    const path = window.location.pathname;
    if (path === '/cuam' || path.startsWith('/cuam/')) return '/cuam';
    return '';
  }, [isStandAlone]);

  return useSyncExternalStore(subscribe, getSnapshot, () => '');
}
